import type { ResultOf } from '@graphql-typed-document-node/core'
import { useCallback } from 'react'
import type { MouseEvent, PropsWithChildren } from 'react'
import { GuideFusuma } from '~/components/modal/GuideFusuma'
import ReviewSummary from '~/components/reviews/ReviewSummary'
import { RestaurantRating } from '~/features/guide/restaurantRating.frag'
import ReviewList from '~/features/guide/sd/review/ReviewList'
import { MEAL_PERIOD_LABEL } from '~/lib/constants/constants'
import { getFragmentData } from '~/lib/graphql'
import type { FragmentType } from '~/lib/graphql'
import type { MealPeriod } from '~/lib/graphql/graphql'
import type { RestaurantId } from '~/lib/graphql/scalars'
import { useIsPC } from '~/lib/ua'
import { Link } from '~/navigation'
import { css, sva } from '~/styled-system/css'
import { createTabs } from '~/ui/Tabs'
import {
  PAGE_SIZE,
  useInitVariables,
  useListenReviewCriteriaChange,
  useMealPeriods,
  usePage,
  useReviews,
  useSelectedMealPeriod,
  useSetMealPeriod,
  useSetPage,
  useTotalCount,
} from './review'

import { useLocation } from '@remix-run/react'
import { type EnvType, useEnv } from '~/env'
import { groupDigits } from '~/lib/base/format'
import { vstack } from '~/styled-system/patterns'
import { Pager as UiPager, pageToIndexRange } from '~/ui/Pager'

type ReviewsFusumaProps = {
  data: FragmentType<typeof RestaurantRating>
  page: number
  mealPeriod?: MealPeriod
}

export function ReviewsFusuma({ data, page, mealPeriod }: ReviewsFusumaProps) {
  const restaurantRating = getFragmentData(RestaurantRating, data)
  useInitVariables({ restaurantId: restaurantRating.restaurantId, page, mealPeriod })
  return (
    <GuideReviewFusuma
      restaurantId={restaurantRating.restaurantId}
      rating={restaurantRating.rating}
      name={restaurantRating.name ?? ''}
      isReviewHidden={restaurantRating.isReviewHidden}
    />
  )
}

type Props = {
  restaurantId: RestaurantId
  rating: ResultOf<typeof RestaurantRating>['rating']
  name: string
  isReviewHidden: boolean
}

const styles = sva({
  slots: [
    'heading',
    'total',
    'tab',
    'summary',
    'list',
    'item',
    'title',
    'rating',
    'reviews',
    'close',
  ],
  base: {
    heading: {
      fontSize: 'lg',
      fontWeight: 'bold',
      _sd: {
        mt: '3',
        px: '3',
        textAlign: 'center',
      },
      _pc: {
        px: '8',
      },
    },
    tab: {
      marginBottom: '5',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'between',
    },
    summary: {
      _sd: {
        spaceY: '3',
        px: '3',
        py: '5',
      },
      _pc: {
        display: 'flex',
        alignItems: 'center',
        columnGap: '10',
        mt: '5',
        mb: '8',
        px: '8',
      },
    },
    total: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      rowGap: '2',
      _pc: {
        alignItems: 'start',
        rowGap: '3',
      },
    },
    list: {
      _sd: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: 'medium',
        px: '8',
        py: '3',
        textAlign: 'center',
      },
      _pc: {
        display: 'grid',
        gridTemplateColumns: '2',
        gap: '5',
        w: '[380px]',
      },
    },
    item: {
      _pc: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        pb: '2',
        borderBottom: 'light',
      },
    },
    title: {
      fontSize: 'sm',
      _pc: {
        fontSize: 'base',
      },
    },
    rating: {
      fontSize: 'lg',
      fontWeight: 'bold',
      _pc: {
        fontSize: 'xl',
      },
    },
    reviews: {
      bg: 'gray.100',
      _sd: {
        spaceY: '3',

        p: '3',
      },
      _pc: {
        p: '8',
        spaceY: '5',
      },
    },
    close: {
      display: 'inline-block',
      w: 'full',
      rounded: 'md',
      border: '[1px solid]',
      borderColor: 'button.secondary.solid',
      bg: 'white',
      mt: '8',
      p: '1',
      py: '2',
      fontSize: 'xl',
      color: 'button.secondary.solid',
      _pc: {
        w: 'auto',
        px: '4',
        py: '2',
      },
    },
  },
})

const Tabs = createTabs<MealPeriod>()

function GuideReviewFusuma({ restaurantId, rating, name, isReviewHidden }: Props) {
  const { reviews, onClick, selected, isPC, mealPeriods, href } = useReview()
  const classes = styles()

  return (
    <GuideFusuma title="クチコミ" headerColor={isPC ? 'white' : 'neutral'} layout="full">
      {isReviewHidden ? (
        <>
          <h1 className={classes.heading}>
            <Link
              to={`/${restaurantId}`}
              prefetch="viewport"
              className={css({ color: 'gray.800' })}
            >
              {name}
            </Link>
          </h1>
          <div
            className={css({
              mt: '5',
              px: '3',
              textAlign: 'center',
              _pc: { p: '8' },
            })}
          >
            <p className={css({ fontFamily: 'serif', fontSize: '5xl', color: 'tertiary' })}>
              Sorry...
            </p>
            <p className={css({ mt: '3', _pc: { fontSize: 'md' } })}>
              このレストランのクチコミは一時的に
              {!isPC && <br />}
              非掲載となっております。
            </p>
            {!isPC && (
              <Link to={`/${restaurantId}`} prefetch="viewport" className={classes.close}>
                閉じる
              </Link>
            )}
          </div>
        </>
      ) : (
        <>
          <h1 className={classes.heading}>
            <Link
              to={`/${restaurantId}`}
              prefetch="viewport"
              className={css({ color: 'gray.800' })}
            >
              {name}
            </Link>
          </h1>
          {rating?.totalCount && rating.totalCount >= 3 ? (
            <div className={classes.summary}>
              {/* TODO:ratingが返ってこなかった場合の分岐処理 */}
              <ReviewSummary
                restaurantId={restaurantId}
                totalCount={rating.totalCount}
                totalValue={rating.totalValue}
                linkClassName={classes.total}
                isAggregateRatingDisabled={true}
                vertical={true}
                size="lg"
              />
              <div className={classes.list}>
                <div className={classes.item}>
                  <div className={classes.title}>料理・味</div>
                  <div className={classes.rating}>{displayRating(rating.food)} </div>
                </div>
                <div className={classes.item}>
                  <div className={classes.title}>雰囲気</div>
                  <div className={classes.rating}>{displayRating(rating.atmosphere)}</div>
                </div>
                <div className={classes.item}>
                  <div className={classes.title}>サービス</div>
                  <div className={classes.rating}>{displayRating(rating.service)} </div>
                </div>
                <div className={classes.item}>
                  <div className={classes.title}>コスパ</div>
                  <div className={classes.rating}>{displayRating(rating.costPerformance)}</div>
                </div>
              </div>
              {/* <ReviewPickupWords /> */}
            </div>
          ) : (
            <div className={css({ px: '3', py: '5', _pc: { p: '8' } })}>
              <p className={css({ color: 'gray.600' })}>
                規定評価数に達していないため、総合点数を非表示としています
              </p>
            </div>
          )}
          {mealPeriods.length > 1 && (
            <div className={css({ px: { base: '3', _pc: '8' } })}>
              <Tabs.Root theme="border" semi size={isPC ? 'default' : 'sm'} value={selected}>
                {mealPeriods.map((mealPeriod) => (
                  <Tabs.Tab key={mealPeriod} value={mealPeriod} onClick={onClick} asChild>
                    <a href={href(mealPeriod)}>{MEAL_PERIOD_LABEL[mealPeriod]}</a>
                  </Tabs.Tab>
                ))}
              </Tabs.Root>
            </div>
          )}
          {rating?.totalCount && rating.totalCount >= 1 ? (
            <div className={classes.reviews}>
              <ReviewList reviews={reviews} isPc={isPC} />
            </div>
          ) : (
            <></>
          )}
          <div
            className={css({ display: 'flex', justifyContent: 'center', bg: 'gray.100', pb: '5' })}
          >
            <Pager />
          </div>
        </>
      )}
    </GuideFusuma>
  )
}

function useReview() {
  const reviews = useReviews()
  const setMealPeriod = useSetMealPeriod()
  const onClick = useCallback(
    (v: MealPeriod, e: MouseEvent<HTMLElement>) => {
      e.preventDefault()
      setMealPeriod(v)
    },
    [setMealPeriod]
  )
  const selected = useSelectedMealPeriod()

  const { pathname } = useLocation()
  const env = useEnv()
  const href = useCallback(
    (mealPeriod: MealPeriod) => {
      return createUrl(pathname, 1, mealPeriod, env)
    },
    [env, pathname]
  )
  const onChange = useCallback(
    (page: number, mealPeriod?: MealPeriod) => {
      const url = createUrl(pathname, page, mealPeriod, env)
      // 本来は navigation に含めるべきだが、ふすま内で自身に遷移する transition が発火すると
      // ふすまが閉じてしまい復帰できないため、直接 url を書き換える
      history.replaceState(history.state, '', url)
    },
    [env, pathname]
  )
  useListenReviewCriteriaChange(onChange)
  const isPC = useIsPC()
  const mealPeriods = useMealPeriods()

  return { reviews, onClick, selected, isPC, mealPeriods, href }
}

function createUrl(
  href: string,
  page: number,
  mealPeriod: MealPeriod | undefined,
  env: EnvType
): string {
  const url = new URL(`${env.RESTAURANT_IKYU_ORIGIN}${href}`)
  url.searchParams.delete('page')
  url.searchParams.delete('meal_period')
  if (page >= 2) {
    url.searchParams.set('page', String(page))
  }
  if (mealPeriod !== undefined && mealPeriod !== 'ALL') {
    url.searchParams.set('meal_period', mealPeriod.toLowerCase())
  }
  return url.toString()
}

function Pager() {
  const totalCount = useTotalCount()
  const page = usePage()
  const rankRange = pageToIndexRange(page, totalCount, PAGE_SIZE)
  if (!rankRange) {
    return null
  }
  if (totalCount <= PAGE_SIZE) {
    return null
  }
  const [start, end] = rankRange
  return (
    <div className={vstack({ gap: '2' })}>
      <UiPager
        current={page}
        total={totalCount}
        pageSize={PAGE_SIZE}
        around={2}
        showPrevNext
        PageButtonTrigger={PagerTrigger}
      />
      <p>
        {start}〜{end}件 / 全{groupDigits(totalCount)}件
      </p>
    </div>
  )
}

function PagerTrigger({ page, children }: PropsWithChildren<{ page: number }>) {
  const set = useSetPage()
  const selected = useSelectedMealPeriod()
  const onClick = useCallback(
    (e: MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault()
      set(page)
    },
    [set, page]
  )
  const { pathname } = useLocation()
  const env = useEnv()
  const href = createUrl(pathname, page, selected, env)
  return (
    <a href={href} onClick={onClick}>
      {children}
    </a>
  )
}

function displayRating(rating: Number) {
  if (rating === 0) {
    return '-'
  }
  return rating.toFixed(2)
}
